import { useCallback, useRef } from 'react';

import { expandPrompt } from '@/api/generation';
import { pushEvent } from '@/hooks/useAnalytics';

interface EntryAIHelpBinding {
  profile: string | null;
  mode: string;
  toolId: string;
  userId: string | null;
}

interface UseEntryAIHelpOptions {
  profile?: string | null;
  mode: string;
  toolId?: string;
  user?: { userId?: string; isVip?: boolean } | null;
}

function normalizeProfile(profile: string | null | undefined): string | null {
  const normalized = profile?.trim();
  return normalized ? normalized : null;
}

function bindingsMatch(current: EntryAIHelpBinding, requested: EntryAIHelpBinding): boolean {
  return (
    current.profile === requested.profile
    && current.mode === requested.mode
    && current.toolId === requested.toolId
    && current.userId === requested.userId
  );
}

export function useEntryAIHelp({
  profile,
  mode,
  toolId = mode,
  user,
}: UseEntryAIHelpOptions) {
  const aiHelpProfile = normalizeProfile(profile);
  const aiHelpDisabled = !aiHelpProfile || !user;
  const userId = user?.userId ?? null;
  const bindingRef = useRef<EntryAIHelpBinding>({
    profile: aiHelpProfile,
    mode,
    toolId,
    userId,
  });
  bindingRef.current = {
    profile: aiHelpProfile,
    mode,
    toolId,
    userId,
  };

  const expandEntryPrompt = useCallback(async (currentPrompt: string): Promise<string | null> => {
    const requested = bindingRef.current;
    if (!requested.profile || !user) throw new Error('ai_help_unavailable');

    let data: Awaited<ReturnType<typeof expandPrompt>>;
    try {
      data = await expandPrompt(currentPrompt, requested.mode, requested.toolId);
    } catch (err) {
      if (!bindingsMatch(bindingRef.current, requested)) return null;
      throw err;
    }
    if (!bindingsMatch(bindingRef.current, requested)) return null;

    pushEvent('generate_ai_help_click', requested.userId ?? undefined, {
      tool_id: requested.toolId,
    });
    return data.expanded_prompt;
  }, [user]);

  return {
    aiHelpProfile,
    aiHelpDisabled,
    expandEntryPrompt,
  };
}
